'use client'
import { useEffect, useRef, useState } from 'react'
import { Icons } from '../Icon/constants'
import Icon from '../Icon/Icon'
import { useTranslation } from '../../context/ConfigContext'
import useCurrentLocale from '../../../core/hook/useCurrentLocale'
import { useQuery } from '@tanstack/react-query'

export function QuickSearch() {
  const [open, setOpen] = useState(false)
  const ns = 'quicksearch'
  const t = useTranslation()
  const [q, setQ] = useState('')
  const locale = useCurrentLocale()

  const payloadUrl = new URL(process.env.NEXT_PUBLIC_PAYLOAD_API_URL as string)

  const resultsElRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    setTimeout(() => {
      const inputEl = document.getElementById(`${ns}-input`)
      inputEl?.focus()
    }, 30)
    if (!document.body.classList.contains('overflow-hidden')) {
      document.body.classList.add('overflow-hidden')
      return () => {
        document.body.classList.remove('overflow-hidden')
      }
    }
  }, [open])

  const resultsQuery = useQuery({
    queryKey: [ns, q, locale],
    async queryFn() {
      if (q.length === 0) {
        return ''
      }
      const params = new URLSearchParams()
      params.set('props', JSON.stringify({ query: q }))
      params.set('locale', locale)
      const response = await fetch(
        `${payloadUrl.origin}/astro/partials/QuickSearchResults?${params.toString()}`,
      )
      if (!response.ok) {
        return ''
      }
      return response.text()
    },
    keepPreviousData: true,
  })

  const resultsEl = resultsElRef.current
  if (resultsEl && resultsQuery.data) {
    resultsEl.innerHTML = resultsQuery.data
  }

  return (
    <>
      <button
        id='quicksearch-open-btn'
        className='size-8 flex items-center justify-center'
        onClick={() => setOpen(true)}>
        <Icon className='size-6 stroke-black lg:stroke-none' iconName={Icons.SEARCH} width={32} height={32} />
      </button>
      {open && (
        <div className='fixed top-0 bottom-0 lg:bottom-auto w-full left-0 z-30 bg-white flex flex-col'>
          <div className='flex flex-wrap lg:flex-nowrap gap-4 py-5 lg:py-8 border-solid border-b border-b-msk-gray-100'>
            <form
              id={`${ns}-form`}
              className='h-10 order-2 lg:order-1 flex gap-4 items-center border-b border-b-msk-gray-100 lg:border-b-0 w-full mx-8'
              method='GET'
              action={`/${locale}/catalogsearch/result`}>
              <label htmlFor={`${ns}-input`} className='lg:hidden'>
                <Icon iconName={Icons.SEARCH} width={32} height={32} className='size-6' />
              </label>
              <input
                id={`${ns}-input`}
                type='text'
                className='w-full placeholder:text-black h-6 outline-none'
                placeholder={t('What are you looking for?')}
                value={q}
                onChange={(ev) => setQ(ev.target.value)}
                name='q'
              />
              {q.length > 0 && (
                <button
                  onClick={() => setQ('')}
                  id={`${ns}-clear-btn`}
                  type='button'
                  className='text-msk-gray-600'>
                  {t('Cancel')}
                </button>
              )}
            </form>
            <div className='hidden lg:flex w-full lg:w-auto order-1 lg:order-2 pe-4 lg:pe-8'>
              <button
                className='ms-auto'
                aria-controls={ns}
                aria-expanded='false'
                type='button'
                value='close'
                onClick={() => setOpen(false)}>
                <Icon iconName={Icons.CLOSE} className='size-6' />
              </button>
            </div>
          </div>
          <div
            id={`${ns}-results`}
            className='flex-grow overflow-auto pt-8 lg:pt-0'
            ref={resultsElRef}></div>
          <div className='flex-none lg:hidden p-8'>
            <button
              aria-controls={`${ns}-dialog`}
              aria-expanded='false'
              className={`${ns}-close-btn lg:start-auto lg:end-2 gap-2 uppercase inline-flex text-p2-sm lg:text-p2 items-center`}
              onClick={() => setOpen(false)}>
              {t('Close')}
              <Icon iconName={Icons.CLOSE} className='size-6' />
            </button>
          </div>
        </div>
      )}
    </>
  )
}
